/**
 * useBranding Hook
 *
 * Owns the branding state for one site: reads both records, holds the colours
 * being edited, and performs save, publish, reset and asset changes.
 *
 * A feature hook rather than a context provider, because no other tab needs this
 * state — a provider would be scope without a beneficiary.
 *
 * Requirements: 2.1, 2.3, 2.4, 2.6, 2.8, 3.4, 3.8, 5.7, 5.8, 5.9, 5.10, 6.2,
 * 6.4, 6.5, 7.3, 8.7, 8.9, 8.11, 9.2, 9.4, 11.3, 11.4, 11.10
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { track } from '../../analytics';
import type {
  BrandingPublishedAttributes,
  BrandingResetAttributes,
} from '../../analytics';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import {
  deleteBrandAsset,
  fetchBranding,
  publishBranding,
  resetBranding,
  saveBrandingDraft,
  uploadBrandAsset,
  type BrandingApiClientConfig,
  type BrandingFailure,
} from '../api/brandingApiClient';
import type { BrandRecordResponse } from '../models/apiModels';
import { toBrandRecord, toBrandingState } from '../models/brandingAdapter';
import {
  EMPTY_COLORS_DRAFT,
  EMPTY_TYPOGRAPHY,
  toColorsDraft,
  type BrandAssetSlot,
  type BrandColorSlot,
  type BrandColorsDraft,
  type BrandRecord,
} from '../models/domainModels';
import { isBrandColor } from '../utils/brandColors';
import { buildBrandingWriteRequest } from '../utils/brandRequest';
import { checkContrast, type ContrastFinding } from '../utils/contrastCheck';
import { parseBrandingError, type ParsedBrandingError } from '../utils/errorMessage';
import { describePngRejection, validatePng } from '../utils/pngValidation';

/**
 * Why an action could not be performed on authentication grounds.
 *
 * Kept separate from `error` because the two need different remedies: `expired`
 * should send the operator to sign in again, whereas `forbidden` means the
 * account is known and simply is not an admin, so re-authenticating would change
 * nothing.
 */
export type BrandingAuthError = 'expired' | 'forbidden';

export interface UseBrandingResult {
  readonly published: BrandRecord | null;
  readonly draft: BrandRecord | null;
  /** The colours currently in the editor, as raw input strings. */
  readonly colors: BrandColorsDraft;
  readonly hasUnsavedChanges: boolean;
  readonly hasPendingDraft: boolean;
  readonly contrastFindings: readonly ContrastFinding[];
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  readonly isPublishing: boolean;
  /**
   * True while any action that writes stored branding is in flight.
   *
   * Every mutating action shares one writer slot, so the UI can disable all of them
   * together rather than each guarding only itself.
   */
  readonly isMutating: boolean;
  /** False while a colour is complete but not a valid `#RRGGBB`, or another write is in flight. */
  readonly canSave: boolean;
  readonly error: ParsedBrandingError | null;
  readonly authError: BrandingAuthError | null;
  /** A file rejected before any request was made. */
  readonly assetError: string | null;
  readonly setColor: (slot: BrandColorSlot, value: string) => void;
  readonly save: () => Promise<void>;
  readonly publish: () => Promise<void>;
  readonly reset: () => Promise<void>;
  readonly uploadAsset: (
    slot: BrandAssetSlot,
    file: File,
    alt: string
  ) => Promise<void>;
  readonly removeAsset: (slot: BrandAssetSlot) => Promise<void>;
}

/** True when any field holds something that is neither empty nor a valid colour. */
const hasInvalidColor = (colors: BrandColorsDraft): boolean =>
  Object.values(colors).some((value) => value.length > 0 && !isBrandColor(value));

const sameColors = (a: BrandColorsDraft, b: BrandColorsDraft): boolean =>
  (Object.keys(a) as BrandColorSlot[]).every((slot) => a[slot] === b[slot]);

export function useBranding(siteId: string | undefined): UseBrandingResult {
  const [published, setPublished] = useState<BrandRecord | null>(null);
  const [draft, setDraft] = useState<BrandRecord | null>(null);
  const [colors, setColors] = useState<BrandColorsDraft>(EMPTY_COLORS_DRAFT);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState<ParsedBrandingError | null>(null);
  const [authError, setAuthError] = useState<BrandingAuthError | null>(null);
  const [assetError, setAssetError] = useState<string | null>(null);

  const { getAccessToken } = useAuth();

  // A ref so the load effect does not re-run when this callback changes identity.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Reset on mount as well as clear on unmount, so a StrictMode remount does not
  // leave the hook permanently unable to set state.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * One writer at a time, across *all* mutating actions.
   *
   * Not just a second save: save, publish, reset and the asset changes all write to
   * the same two stored records, and whichever response landed last would win. A
   * save resolving after a reset had already cleared the brand would silently
   * resurrect the draft the operator had just thrown away.
   *
   * A ref rather than the `isSaving`/`isPublishing` state because a state update has
   * not landed by the time a second click can arrive.
   */
  const mutatingRef = useRef(false);
  const [isMutating, setIsMutating] = useState(false);

  /** Set from render, below, so `load` can read it without being rebuilt. */
  const hasUnsavedChangesRef = useRef(false);

  const beginMutation = useCallback((): boolean => {
    if (mutatingRef.current) return false;
    mutatingRef.current = true;
    setIsMutating(true);
    return true;
  }, []);

  const endMutation = useCallback(() => {
    mutatingRef.current = false;
    if (mountedRef.current) setIsMutating(false);
  }, []);

  const apiConfig = useCallback(
    (): BrandingApiClientConfig => ({
      apiHost: getAppConfig().apiHost,
      getAccessToken: getAccessTokenRef.current,
    }),
    []
  );

  /**
   * The newest record for the site.
   *
   * An edit always builds on this: the draft when there is one, otherwise what is
   * published. It is what the editor loads from, and where the typography that
   * must survive a colours-only save comes from.
   */
  const workingBrand = draft ?? published;

  /** Clear the failure state so a retry starts from a clean slate. */
  const clearFailures = useCallback(() => {
    setError(null);
    setAuthError(null);
    setAssetError(null);
  }, []);

  /** Translate a failure into the state the UI renders. */
  const applyFailure = useCallback((failure: BrandingFailure) => {
    switch (failure.status) {
      case 'unauthorized':
        setAuthError('expired');
        break;
      case 'forbidden':
        setAuthError('forbidden');
        break;
      case 'invalid':
      case 'error':
        setError(parseBrandingError(failure.message));
        break;
      case 'unbranded':
      case 'nothing_pending':
        // Handled by the callers, which know what these mean in their context.
        break;
    }
  }, []);

  /**
   * Read both records.
   *
   * `isInitial` distinguishes the first read from a later refresh, and controls two
   * things that must not happen on a refresh:
   *
   * - `isLoading`, which the page uses to replace the whole editor with a loading
   *   line. Toggling it mid-session would flash the editor away and back.
   * - adopting the stored colours, which would discard whatever the operator has
   *   typed since. A refresh happens because the *server* changed, not because the
   *   operator abandoned their edits.
   */
  const load = useCallback(
    async (isInitial: boolean) => {
      if (!siteId) {
        setIsLoading(false);
        return;
      }

      if (isInitial) setIsLoading(true);

      // On a refresh, only adopt stored colours when there is nothing to lose.
      const adoptColors = isInitial || !hasUnsavedChangesRef.current;

      try {
        const result = await fetchBranding(siteId, apiConfig());
        if (!mountedRef.current) return;

        if (result.status === 'ok') {
          const state = toBrandingState(result.data);
          const working = state.draft ?? state.published;
          setPublished(state.published);
          setDraft(state.draft);
          if (adoptColors) {
            setColors(working ? toColorsDraft(working.colors) : EMPTY_COLORS_DRAFT);
          }
          clearFailures();
        } else if (result.status === 'unbranded') {
          // Not an error: a site with neither record is an ordinary starting point.
          setPublished(null);
          setDraft(null);
          if (adoptColors) setColors(EMPTY_COLORS_DRAFT);
          clearFailures();
        } else {
          applyFailure(result);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setError(
          parseBrandingError(
            err instanceof Error ? err.message : 'Failed to load branding'
          )
        );
      } finally {
        if (mountedRef.current && isInitial) setIsLoading(false);
      }
    },
    [siteId, apiConfig, clearFailures, applyFailure]
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  const setColor = useCallback((slot: BrandColorSlot, value: string) => {
    setColors((previous) => ({ ...previous, [slot]: value }));
  }, []);

  /**
   * Apply a returned record as the draft.
   *
   * `syncColors` is true only after a colours save: there, the returned record is
   * the canonical version of what the operator just saved, so adopting it is what
   * clears "unsaved changes". After an asset change it must be false — the
   * operator may have unsaved colour edits in progress, and overwriting them
   * with the stored values would silently discard their work.
   */
  const applyDraft = useCallback(
    (record: BrandRecordResponse, syncColors: boolean) => {
      const domain = toBrandRecord(record);
      setDraft(domain);
      if (syncColors) setColors(toColorsDraft(domain.colors));
    },
    []
  );

  const save = useCallback(async () => {
    if (!siteId) return;
    if (hasInvalidColor(colors)) return;
    if (!beginMutation()) return;

    setIsSaving(true);
    clearFailures();

    try {
      const body = buildBrandingWriteRequest(
        siteId,
        colors,
        // Carried through untouched: this app has no typography UI, and omitting
        // the field would clear a club's typography on every colour save.
        workingBrand?.typography ?? EMPTY_TYPOGRAPHY
      );

      const result = await saveBrandingDraft(siteId, body, apiConfig());
      if (!mountedRef.current) return;

      if (result.status === 'ok') {
        applyDraft(result.data, true);
      } else {
        // The operator's edits are deliberately left in place: a rejected write
        // leaves both stored records unchanged, so the edits are still the
        // accurate reflection of what they were trying to save.
        applyFailure(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(
          parseBrandingError(err instanceof Error ? err.message : 'Failed to save')
        );
      }
    } finally {
      endMutation();
      if (mountedRef.current) setIsSaving(false);
    }
  }, [
    siteId,
    colors,
    workingBrand,
    apiConfig,
    clearFailures,
    applyFailure,
    applyDraft,
    beginMutation,
    endMutation,
  ]);

  const publish = useCallback(async () => {
    if (!siteId) return;
    if (!beginMutation()) return;

    setIsPublishing(true);
    clearFailures();

    try {
      const result = await publishBranding(siteId, apiConfig());
      if (!mountedRef.current) return;

      if (result.status === 'ok') {
        // Publishing carries the draft over and clears it, so there is no longer
        // anything pending. Editor colours are deliberately not re-synced: they
        // already match if nothing was unsaved, and if something was unsaved it
        // must stay visible rather than be silently dropped.
        const record = toBrandRecord(result.data);
        setPublished(record);
        setDraft(null);

        const attributes: BrandingPublishedAttributes = {
          SiteId: siteId,
          Version: record.version,
        };
        track('Pocket Admin Branding Published', attributes);
      } else if (result.status === 'nothing_pending') {
        // Re-read before explaining, because someone else may have published this
        // draft and the state on screen is stale. The message is set *after* the
        // read on purpose: a successful read clears stale failures, so setting it
        // first would wipe the very explanation we are trying to show.
        await load(false);
        if (!mountedRef.current) return;
        setError(
          parseBrandingError(
            'There is nothing pending to publish. Someone may have published this draft already.'
          )
        );
      } else {
        applyFailure(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(
          parseBrandingError(
            err instanceof Error ? err.message : 'Failed to publish'
          )
        );
      }
    } finally {
      endMutation();
      if (mountedRef.current) setIsPublishing(false);
    }
  }, [
    siteId,
    apiConfig,
    clearFailures,
    applyFailure,
    load,
    beginMutation,
    endMutation,
  ]);

  const reset = useCallback(async () => {
    if (!siteId) return;
    if (!beginMutation()) return;

    clearFailures();

    try {
      const result = await resetBranding(siteId, apiConfig());
      if (!mountedRef.current) return;

      if (result.status === 'ok') {
        // Captured before clearing: whether a live brand was removed or only an
        // unshipped draft was tidied away is the interesting part of a reset.
        const attributes: BrandingResetAttributes = {
          SiteId: siteId,
          HadPublishedBrand: published !== null,
        };

        setPublished(null);
        setDraft(null);
        setColors(EMPTY_COLORS_DRAFT);

        track('Pocket Admin Branding Reset', attributes);
      } else {
        applyFailure(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(
          parseBrandingError(err instanceof Error ? err.message : 'Failed to reset')
        );
      }
    } finally {
      endMutation();
    }
  }, [
    siteId,
    published,
    apiConfig,
    clearFailures,
    applyFailure,
    beginMutation,
    endMutation,
  ]);

  const uploadAsset = useCallback(
    async (slot: BrandAssetSlot, file: File, alt: string) => {
      if (!siteId) return;

      clearFailures();

      // An ArrayBuffer rather than a stream, so the 401 retry can resend it.
      const buffer = await file.arrayBuffer();
      const rejection = describePngRejection(validatePng(new Uint8Array(buffer)));
      if (rejection !== null) {
        // Rejected before any request: an oversized file costs nothing, and the
        // message names the specific limit that failed.
        if (mountedRef.current) setAssetError(rejection);
        return;
      }

      // Claimed only once the file is known good, so a rejected file never occupies
      // the writer slot.
      if (!beginMutation()) return;

      try {
        const result = await uploadBrandAsset(siteId, slot, buffer, alt, apiConfig());
        if (!mountedRef.current) return;

        if (result.status === 'ok') {
          applyDraft(result.data, false);
        } else {
          // A failed upload leaves the stored record untouched, so the previously
          // stored logo is still what is displayed.
          applyFailure(result);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(
            parseBrandingError(
              err instanceof Error ? err.message : 'Failed to upload logo'
            )
          );
        }
      } finally {
        endMutation();
      }
    },
    [siteId, apiConfig, clearFailures, applyFailure, applyDraft, beginMutation, endMutation]
  );

  const removeAsset = useCallback(
    async (slot: BrandAssetSlot) => {
      if (!siteId) return;
      if (!beginMutation()) return;

      clearFailures();

      try {
        const result = await deleteBrandAsset(siteId, slot, apiConfig());
        if (!mountedRef.current) return;

        if (result.status === 'ok') {
          applyDraft(result.data, false);
        } else {
          applyFailure(result);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(
            parseBrandingError(
              err instanceof Error ? err.message : 'Failed to remove logo'
            )
          );
        }
      } finally {
        endMutation();
      }
    },
    [siteId, apiConfig, clearFailures, applyFailure, applyDraft, beginMutation, endMutation]
  );

  const hasUnsavedChanges = useMemo(
    () =>
      !sameColors(
        colors,
        workingBrand ? toColorsDraft(workingBrand.colors) : EMPTY_COLORS_DRAFT
      ),
    [colors, workingBrand]
  );

  // Read by `load` on a refresh, which must not adopt stored colours over edits in
  // progress. A ref because `load` is a stable callback and must not be rebuilt —
  // and therefore re-run by its effect — every time a character is typed.
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  const contrastFindings = useMemo(() => checkContrast(colors), [colors]);

  return useMemo(
    () => ({
      published,
      draft,
      colors,
      hasUnsavedChanges,
      hasPendingDraft: draft !== null,
      contrastFindings,
      isLoading,
      isSaving,
      isPublishing,
      isMutating,
      // Blocked while any other action is writing, not merely while saving: they
      // all write the same two records.
      canSave: !isMutating && !hasInvalidColor(colors),
      error,
      authError,
      assetError,
      setColor,
      save,
      publish,
      reset,
      uploadAsset,
      removeAsset,
    }),
    [
      published,
      draft,
      colors,
      hasUnsavedChanges,
      contrastFindings,
      isLoading,
      isSaving,
      isPublishing,
      isMutating,
      error,
      authError,
      assetError,
      setColor,
      save,
      publish,
      reset,
      uploadAsset,
      removeAsset,
    ]
  );
}
